import React, { useCallback, useMemo, useState } from 'react';
import {
  useBatchCompetitorMutes,
  useCreateBatchCompetitorMute,
  useFilterLeagues,
  useFilterMarketIds,
  useRemoveBatchCompetitorMute,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { useHomebaseModal } from '../components/HomebaseModal';
import './BatchSuspensionsPage.css';

const SCOPES = [
  { value: 'league', label: 'One league' },
  { value: 'league_wager', label: 'League + market type' },
  { value: 'all', label: 'Entire bucket' },
];

const SPORT_BUCKETS = [
  { value: 'soccer', label: 'Soccer' },
  { value: 'basketball', label: 'Basketball' },
  { value: 'esports', label: 'Esports' },
];

function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (Array.isArray(d?.detail)) {
    return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  }
  return err?.message || 'Request failed';
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function BatchCompetitorMutesPage() {
  const { user } = useAuth();
  const { confirm, ModalLayer } = useHomebaseModal();
  const [sportBucket, setSportBucket] = useState('soccer');
  const [listSportFilter, setListSportFilter] = useState('all');
  const [scope, setScope] = useState('league');
  const [league, setLeague] = useState('');
  const [wagerType, setWagerType] = useState('');
  const [reason, setReason] = useState('Batch competitor mute');
  const [createdBy, setCreatedBy] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const [fanoutWriters, setFanoutWriters] = useState(true);
  const [lastError, setLastError] = useState(null);
  const [lastSuccess, setLastSuccess] = useState(null);

  const listBucketParam = listSportFilter === 'all' ? null : listSportFilter;
  const { data, isLoading, isError, error, refetch } = useBatchCompetitorMutes(
    true,
    listBucketParam
  );
  const mutes = useMemo(
    () => (Array.isArray(data?.mutes) ? data.mutes : []),
    [data]
  );

  const { data: leagueOptions = [], isLoading: leaguesLoading } = useFilterLeagues();
  const { data: marketIdOptions = [], isLoading: marketIdsLoading } = useFilterMarketIds({
    enabled: scope === 'league_wager',
  });

  const createMut = useCreateBatchCompetitorMute();
  const removeMut = useRemoveBatchCompetitorMute();

  const showLeague = scope === 'league' || scope === 'league_wager';
  const showWager = scope === 'league_wager';
  const leagueMissing = showLeague && !String(league).trim();
  const wagerMissing = showWager && !String(wagerType).trim();
  const needsConfirmAll = scope === 'all' && !confirmAll;
  const busy = createMut.isPending || removeMut.isPending;

  const payload = useMemo(() => {
    const p = {
      sport_bucket: sportBucket,
      scope,
      reason: reason.trim() || 'Batch competitor mute',
      created_by: createdBy.trim() || user?.email || 'dashboard',
      confirm_all_markets: scope === 'all' ? confirmAll : false,
      fanout_writers: fanoutWriters,
    };
    if (showLeague) p.league = league.trim() || undefined;
    if (showWager) p.wager_type = wagerType.trim() || undefined;
    return p;
  }, [
    sportBucket,
    scope,
    league,
    wagerType,
    reason,
    createdBy,
    user?.email,
    confirmAll,
    fanoutWriters,
    showLeague,
    showWager,
  ]);

  const runCreate = useCallback(() => {
    setLastError(null);
    setLastSuccess(null);
    createMut.mutate(payload, {
      onSuccess: (res) => setLastSuccess(res),
      onError: (e) => setLastError(formatApiError(e)),
    });
  }, [createMut, payload]);

  const runRemove = useCallback(
    async (id) => {
      const ok = await confirm({
        title: 'End batch competitor mute',
        body: `End rule #${id}? Writers reload rules on the next fan-out (or next deploy / reload-manual-gates).`,
        confirmLabel: 'End rule',
      });
      if (!ok) return;
      setLastError(null);
      setLastSuccess(null);
      removeMut.mutate(id, {
        onSuccess: (res) => setLastSuccess(res),
        onError: (e) => setLastError(formatApiError(e)),
      });
    },
    [confirm, removeMut]
  );

  return (
    <div className="batch-susp-page">
      <header className="batch-susp-header">
        <h1>Batch competitor mute</h1>
        <p className="batch-susp-lede">
          Matching markets stop base-KF z² sync into EntriesKF and z²-gated synthetic order-book ladder
          refresh (same scopes as batch suspension: sport bucket, league, or league + market type). Per-row{' '}
          <code>mute_competitor_signals</code> in the dashboard is OR&apos;d with these rules. Writers pick up
          new rules when <strong>Reload writers</strong> runs (POST reload-manual-gates).
        </p>
      </header>

      <div className="batch-susp-grid">
        <section className="batch-susp-card">
          <h2>New rule</h2>
          <label className="batch-susp-field">
            <span>Sport bucket</span>
            <select
              value={sportBucket}
              onChange={(e) => setSportBucket(e.target.value)}
              disabled={busy}
            >
              {SPORT_BUCKETS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="batch-susp-field">
            <span>Scope</span>
            <select
              value={scope}
              onChange={(e) => {
                const v = e.target.value;
                setScope(v);
                if (v === 'all') {
                  setLeague('');
                  setWagerType('');
                } else if (v === 'league') {
                  setWagerType('');
                }
              }}
              disabled={busy}
            >
              {SCOPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          {showLeague && (
            <label className="batch-susp-field">
              <span>League</span>
              <select
                value={league}
                onChange={(e) => setLeague(e.target.value)}
                disabled={busy || leaguesLoading}
              >
                <option value="">
                  {leaguesLoading ? 'Loading leagues…' : 'Select league'}
                </option>
                {Array.isArray(leagueOptions) &&
                  leagueOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
              </select>
              {league ? (
                <span className="batch-susp-muted batch-susp-field-hint">
                  Value sent to API: <code>{league}</code>
                </span>
              ) : null}
            </label>
          )}
          {showWager && (
            <label className="batch-susp-field">
              <span>Market type</span>
              <select
                value={wagerType}
                onChange={(e) => setWagerType(e.target.value)}
                disabled={busy || marketIdsLoading}
              >
                <option value="">
                  {marketIdsLoading ? 'Loading…' : 'Select market type'}
                </option>
                {Array.isArray(marketIdOptions) &&
                  marketIdOptions.map((mid) => (
                    <option key={mid} value={mid}>
                      {mid}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label className="batch-susp-field">
            <span>Reason</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="batch-susp-field">
            <span>Created by (optional)</span>
            <input
              type="text"
              value={createdBy}
              onChange={(e) => setCreatedBy(e.target.value)}
              placeholder={user?.email || 'defaults to your email'}
              disabled={busy}
            />
          </label>
          {scope === 'all' && (
            <label className="batch-susp-check batch-susp-check-warn">
              <input
                type="checkbox"
                checked={confirmAll}
                onChange={(e) => setConfirmAll(e.target.checked)}
                disabled={busy}
              />
              <span>I confirm: mute competitor sync for <strong>all</strong> markets in this bucket</span>
            </label>
          )}
          <label className="batch-susp-check">
            <input
              type="checkbox"
              checked={fanoutWriters}
              onChange={(e) => setFanoutWriters(e.target.checked)}
              disabled={busy}
            />
            <span>Reload writers in this bucket now</span>
          </label>
          <button
            type="button"
            className="batch-susp-btn batch-susp-btn-primary"
            disabled={
              busy ||
              leagueMissing ||
              wagerMissing ||
              needsConfirmAll ||
              (showWager && marketIdsLoading)
            }
            onClick={runCreate}
          >
            {createMut.isPending ? 'Submitting…' : 'Create rule'}
          </button>
        </section>

        <section className="batch-susp-card">
          <div className="batch-susp-card-head">
            <h2>Active rules</h2>
            <button type="button" className="batch-susp-btn-ghost" onClick={() => refetch()} disabled={isLoading}>
              Refresh
            </button>
          </div>
          <label className="batch-susp-field batch-susp-field-inline">
            <span>Show</span>
            <select
              value={listSportFilter}
              onChange={(e) => setListSportFilter(e.target.value)}
              disabled={busy}
            >
              <option value="all">All sports</option>
              {SPORT_BUCKETS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label} only
                </option>
              ))}
            </select>
          </label>
          {isLoading && <p className="batch-susp-muted">Loading…</p>}
          {isError && (
            <p className="batch-susp-err">{formatApiError(error)}</p>
          )}
          {!isLoading && !isError && mutes.length === 0 && (
            <p className="batch-susp-muted">No active rules.</p>
          )}
          {!isLoading && mutes.length > 0 && (
            <ul className="batch-susp-list">
              {mutes.map((row) => (
                <li key={row.id} className="batch-susp-row">
                  <div className="batch-susp-row-main">
                    <strong>#{row.id}</strong>{' '}
                    <code>{row.sport_bucket || '—'}</code>{' '}
                    <code>{row.scope}</code>
                    {row.league ? ` · ${row.league}` : ''}
                    {row.wager_type ? ` · ${row.wager_type}` : ''}
                    <div className="batch-susp-reason">{row.reason}</div>
                    <div className="batch-susp-meta">
                      {row.created_by || '—'} · since {formatTs(row.valid_from)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="batch-susp-btn batch-susp-btn-danger"
                    disabled={busy}
                    onClick={() => runRemove(row.id)}
                  >
                    End rule
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {lastError && (
        <div className="batch-susp-banner batch-susp-banner-err">{lastError}</div>
      )}
      {lastSuccess && (
        <section className="batch-susp-card batch-susp-result">
          <h3>Last API response</h3>
          <pre className="batch-susp-pre">{JSON.stringify(lastSuccess, null, 2)}</pre>
        </section>
      )}
      <ModalLayer />
    </div>
  );
}
